package com.swetdot.swetgram.ui.design

import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Shapes
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * SDS §6.1. An 8dp system, with one half-step.
 *
 * Typed [Dp], not Int: the previous `object Spacing` held bare Ints, so every call site had to
 * write `.dp` anyway -- and every call site instead wrote a literal. It was referenced zero
 * times. A scale nobody can use without ceremony does not get used.
 */
object Space {
    val none: Dp = 0.dp
    val half: Dp = 4.dp
    val one: Dp = 8.dp
    val two: Dp = 16.dp
    val three: Dp = 24.dp
    val four: Dp = 32.dp
    val six: Dp = 48.dp
    val eight: Dp = 64.dp

    /** SDS §6.2. The horizontal margin every screen starts from. */
    val screenMargin: Dp = 16.dp
}

/** Shared readable-width limits for centered screen content. */
object ContentWidth {
    val profile: Dp = 680.dp
    val settings: Dp = 680.dp
    val tasks: Dp = 680.dp
    val premium: Dp = 520.dp
    val form: Dp = 520.dp
}

/** SDS §7.1. The default is [md]; §7.2 says circles for avatars, indicators and FABs. */
object Radius {
    val none: Dp = 0.dp
    val xs: Dp = 6.dp
    val sm: Dp = 10.dp

    /**
     * The chat-list mockup's search field. A RUNG THE SDS DOES NOT HAVE, added rather than rounded
     * onto one that does -- and that is the whole of this KDoc.
     *
     * The mockup reads "a ROUNDED RECTANGLE, not a pill: the corner radius reads around 12-14dp
     * against a field about 44-48dp tall". §7.1's ladder offers 10 and 16 either side of that band
     * and nothing inside it, and §11.3 puts the search bar on `lg` 20dp, which on a 48dp field is
     * 4dp short of a full pill -- exactly the shape the mockup says it is not.
     *
     * 12 rather than 14: the low end, for the reason [Sizes.avatarChatHeader] took the low end of its
     * own band. It is a multiple of 4 like every other dimension here, and it is further from `lg`,
     * so the difference from what shipped is legible rather than arguable.
     *
     * A NUMBER CHOSEN, NOT MEASURED. The mockup gave a range and this is a pick inside it.
     */
    val field: Dp = 12.dp
    val md: Dp = 16.dp
    val lg: Dp = 20.dp
    val xl: Dp = 24.dp
    val full: Dp = 999.dp
}

/**
 * SDS §6.3. Minimum 44dp, recommended 48dp.
 *
 * These are MINIMUMS, not sizes: a 24dp icon still needs a 48dp box around it. Sizing the box
 * to the glyph is the most common way a design-system-compliant screen ends up failing
 * accessibility, because nothing about it looks wrong.
 */
object TouchTarget {
    val minimum: Dp = 44.dp
    val recommended: Dp = 48.dp
    val buttonHeight: Dp = 48.dp
    val fieldHeight: Dp = 52.dp
    val chatRowHeight: Dp = 72.dp
    val bottomNavHeight: Dp = 64.dp
}

/**
 * How much vertical room a floating-action stack occupies, so a list underneath can reserve it.
 *
 * A FAB is drawn in a `Box` at `BottomEnd` and is NOT part of the list's layout, so the list has
 * to be told to stop short of it. Every screen that got that number by hand got it wrong:
 *
 *  - the Chats inbox reserved 80dp for a TWO-disc stack that is 148dp, so a 56dp accent circle sat
 *    on the last row's timestamp in landscape, and covered a whole row in portrait once an error
 *    banner pushed content down;
 *  - the Discover feed reserved the same 80dp for an 88dp stack, 8dp short;
 *  - the Groups list reserved NOTHING at all.
 *
 * All three were separately written, separately reviewed and separately wrong, which is the case
 * for computing it instead. Pass the disc diameters; the arithmetic mirrors the `Column` every FAB
 * stack is built from -- [Space.two] padding around it, [Space.two] between the discs.
 *
 * A `List` rather than a `vararg` because `Dp` is a value class and Kotlin prohibits varargs of
 * one; the single-disc overload below keeps the common call site short.
 */
fun fabStackHeight(discs: List<Dp>): Dp {
    require(discs.isNotEmpty()) { "a FAB stack with no discs reserves nothing" }
    // (n + 1) gaps: one above the top disc, one below the bottom, one between each pair.
    return Space.two * (discs.size + 1) + discs.fold(0.dp) { total, size -> total + size }
}

/** The one-disc case -- Discover's composer action and the Groups create action. */
fun fabStackHeight(disc: Dp): Dp = fabStackHeight(listOf(disc))

/**
 * The two numbers a floating disc stack needs when a FLOATING BOTTOM BAR is drawn under it.
 *
 * ONE VALUE WITH TWO FIELDS, AND THAT IS THE WHOLE REASON THIS TYPE EXISTS. Returning two `Dp`s
 * from two functions -- or worse, writing them at two call sites -- makes it possible to move one
 * and forget the other, and the two failures are not symmetric:
 *
 *  - the list reserved and the disc did not: the disc sinks behind the bar. On the Discover tab
 *    that disc is the ONLY door to the post composer left in the app, so the consequence is not a
 *    cosmetic overlap -- it is a feature that cannot be reached at all;
 *  - the disc lifted and the list did not: the last row hides under the disc, which is the exact
 *    defect [fabStackHeight] was written for, one layer up.
 *
 * So the pair is computed together, out of one clearance, and handed over as one thing. See
 * [fabStackOverFloatingNav], which is the only thing that builds one.
 */
data class FloatingFabMetrics(
    /** What a list under the stack reserves at its bottom, in its own `contentPadding`. */
    val listReservation: Dp,
    /** How far the stack itself is held off the bottom edge, on top of its own margin. */
    val discLift: Dp,
)

/**
 * [fabStackHeight] plus the room a floating bottom bar occupies, and the lift that keeps the discs
 * out of it.
 *
 * [navClearance] is `swetFloatingNavClearance()` on a screen the floating bar is drawn over, and
 * 0dp on one it is not -- `GroupDiscoverScreen` is a pushed detail route with no bar and states
 * `0.dp` rather than being allowed to stay silent. The 0dp case is the IDENTITY: the reservation is
 * exactly [fabStackHeight] and the lift is nothing, which is what a screen with no bar over it drew
 * before this function existed. That is what makes it safe on a body two screens share.
 */
fun fabStackOverFloatingNav(discs: List<Dp>, navClearance: Dp): FloatingFabMetrics {
    require(navClearance >= 0.dp) { "a floating bar cannot occupy $navClearance" }
    return FloatingFabMetrics(
        listReservation = fabStackHeight(discs) + navClearance,
        discLift = navClearance,
    )
}

/** The one-disc case -- Discover's composer action, which is the only caller today. */
fun fabStackOverFloatingNav(disc: Dp, navClearance: Dp): FloatingFabMetrics =
    fabStackOverFloatingNav(listOf(disc), navClearance)

/**
 * How far the floating bottom navigation's rounded container is held off the screen edges.
 *
 * The chat-list mockup: "not a full-width bar attached to the bottom edge. A FLOATING ROUNDED
 * CONTAINER, inset from the left, right and bottom." The horizontal inset is [Space.screenMargin],
 * so the bar's edges line up with the search field and the row text above it rather than starting a
 * second margin nobody asked for; the vertical one is [Space.one], which is the smaller gap the
 * mockup shows below the bar than beside it.
 *
 * NUMBERS CHOSEN, NOT MEASURED -- the mockup says "inset" and does not say by how much.
 */
val FloatingNavHorizontalInset: Dp = Space.screenMargin
val FloatingNavVerticalInset: Dp = Space.one

/**
 * The END padding of a screen header whose title has an icon button beside it.
 *
 * A 24dp glyph inside a 44dp round target sits 10dp in from that target's edge, so a header padded
 * by the ordinary [Space.screenMargin] puts the GLYPH 26dp from the screen edge while the title on
 * the other side starts at 16dp. The mark reads as inset, and no amount of staring at the header's
 * own code says why -- the extra 10dp is inside a component.
 *
 * So the inset is reduced by half the difference between the target and the glyph, which lands the
 * glyph's own edge on the same 16dp margin as the title. COMPUTED FROM THE TWO SIZES rather than
 * written as `6.dp`, so it follows if either moves.
 *
 * IT WAS DERIVED PRIVATELY AND IDENTICALLY IN TWO FILES -- `ChatListScreen.kt` and
 * `GroupsScreen.kt`, each with a comment saying the shared one belonged here. Two copies of one
 * arithmetic is the state this file exists to end; [fabStackHeight] carries the long version of the
 * same argument, with the three screens that got its number wrong by hand.
 */
val HeaderActionInset: Dp = Space.screenMargin - (TouchTarget.minimum - Sizes.iconMedium) / 2

/**
 * What the floating bottom navigation occupies, DERIVED from its own parts rather than guessed.
 *
 * THE SAME ARITHMETIC [fabStackHeight] EXISTS FOR, ON THE NEXT THING THAT FLOATS. A full-width bar
 * attached to the bottom edge is a layout sibling and needs no reservation from anybody; a bar that
 * floats over content does, and three separate screens have already shipped that number wrong by
 * hand (see [fabStackHeight]). So it is computed once, here, from the bar's own geometry:
 *
 *  - [FloatingNavVerticalInset] above the container and below it,
 *  - [TouchTarget.bottomNavHeight] of icons and labels between them,
 *  - and whatever the system's gesture bar or home indicator claims underneath, which is a runtime
 *    value and therefore a PARAMETER: it is 0dp on a three-button phone, about 24dp on a gesture-
 *    navigation Android device, and about 34dp on an iPhone. Baking any one of those in would be
 *    right on one class of device and wrong on the other two, which is a defect that never shows up
 *    on the machine it was written on.
 *
 * `SwetBottomNav` applies the same three itself, in the same order, so the reservation and the bar
 * cannot disagree by construction. `swetFloatingNavClearance()` in `SwetChrome.kt` is the composable
 * that reads the inset and calls this.
 */
fun floatingNavHeight(systemBarInset: Dp): Dp =
    FloatingNavVerticalInset * 2 + TouchTarget.bottomNavHeight + systemBarInset

/** SDS §4.7 and the avatar scale. */
object Sizes {
    /** 8dp on avatarMedium, 10dp on anything larger (SDS §11.5). */
    val onlineDot: Dp = 8.dp
    val onlineDotLarge: Dp = 10.dp
    val onlineDotBorder: Dp = 2.dp

    /**
     * The unread badge's diameter, in the two sizes the app draws.
     *
     * [unreadBadge] is what `SwetUnreadBadge` has always been -- it was written inline as
     * `Space.two + Space.half`, which is 20dp said the long way round; naming it is the whole change.
     * [unreadBadgeLarge] is the chat-list mockup's, which reads "a CIRCLE ... around 22-24dp".
     *
     * 24 rather than 22: it is the multiple of 4 inside the band, and it is what leaves the two-digit
     * case (`12`, and `99+` at three) room inside the circle rather than forcing the badge to grow
     * into a pill on exactly the rows that have the most unread mail. Both values are MINIMUMS -- the
     * badge is a `defaultMinSize` box that still stretches for a wide count.
     */
    val unreadBadge: Dp = 20.dp
    val unreadBadgeLarge: Dp = 24.dp
    // SDS §11.5. The chat list uses avatarMedium; the profile screen uses avatarProfile.
    val avatarExtraSmall: Dp = 24.dp
    val avatarSmall: Dp = 32.dp

    /**
     * The peer's disc in the conversation header. NOT on §11.5's ladder, and deliberately so.
     *
     * The conversation mockup puts it at "roughly 36-40dp" and the ladder offers 32 and 44, both
     * outside that. 36 is the low end of what was asked for, which is what leaves a 56dp bar
     * (`TouchTarget.recommended + Space.one`) 10dp of air above and below the disc; at 44 the bar
     * has 6dp and the avatar starts reading as the tallest thing on the screen.
     *
     * A NUMBER CHOSEN, NOT MEASURED. The mockup gave a range and this is a pick inside it.
     */
    val avatarChatHeader: Dp = 36.dp

    /**
     * The author's disc on a comment row. 36dp, the same number as [avatarChatHeader] and a
     * SEPARATE TOKEN rather than a second call site on it.
     *
     * The comments mockup puts this disc "around 36dp", which is the same pick [avatarChatHeader]
     * made from its own band. Reusing that token would have been one word shorter and would have
     * made its KDoc false: it names the conversation header as its one call site and argues 36
     * from a 56dp bar's air, an argument that says nothing at all about a comment row. A token
     * whose documented reason does not cover its callers is a token the next reader cannot move.
     *
     * They are free to diverge, and the duplicated `36.dp` is the price of that -- the same trade
     * §11.5's ladder already makes by listing 24, 32 and 44 as separate rungs rather than deriving
     * them from each other.
     *
     * A NUMBER CHOSEN, NOT MEASURED. The mockup gave an approximation and this is a pick at it.
     */
    val avatarComment: Dp = 36.dp
    val avatarMedium: Dp = 44.dp

    /**
     * The conversation row's disc in the chat list. ALSO NOT ON §11.5's LADDER, and for the same
     * kind of reason [avatarChatHeader] is not.
     *
     * The chat-list mockup reads "a circular avatar at the left, larger than the app draws today --
     * it reads 52-56dp". The ladder offers 44 and 64, one below the band and one above it.
     *
     * 52 RATHER THAN 56, and the number is decided by the row rather than by taste.
     * [TouchTarget.chatRowHeight] is 72dp and `SwetListRow` pads it by [Space.one] top and bottom,
     * so 56 leaves the disc touching both paddings with 0dp to spare and the row grows the moment
     * the system font does. At 52 there is 2dp of air above and below, the row keeps its 72dp, and
     * the avatar is still 8dp larger than every other list in the app draws.
     *
     * IT CROSSES TWO THRESHOLDS, AND BOTH ARE LISTED BECAUSE NEITHER IS VISIBLE AT THE CALL SITE.
     *
     *  - `SwetAvatar` picks [onlineDotLarge] 10dp above [avatarMedium], so the presence dot grows
     *    with the disc instead of staying an 8dp speck on a circle a quarter bigger. That is the
     *    mockup's dot, and it comes for free from a rule that already existed. ON PURPOSE.
     *  - `avatarInitialsRole` steps from `titleMedium` to `headlineSmall` above [avatarMedium] too,
     *    so a row whose person has no photo now draws 20sp initials where it drew 16sp. A
     *    CONSEQUENCE RATHER THAN A CHOICE: the mockup says nothing about initials, and this is
     *    recorded so a reader auditing initials sizing is not told the chat list is in the 44dp band.
     *
     * A NUMBER CHOSEN, NOT MEASURED.
     */
    val avatarChatRow: Dp = 52.dp
    val avatarLarge: Dp = 64.dp
    val avatarProfile: Dp = 96.dp
    /**
     * SDS §9.4's icon ladder, all five rungs. Named for the SDS's usage column, not its token
     * name, because the code says `iconSmall` where the spec says `icon.xs`:
     *
     *  - [iconSmall] = `icon.xs` 16dp -- "Metadata, small inline icons"
     *  - [iconSmallMedium] = `icon.sm` 20dp -- "Secondary actions"
     *  - [iconMedium] = `icon.md` 24dp -- "Primary navigation/action icons"
     *  - [iconLarge] = `icon.lg` 32dp -- "Empty states, large actions"
     *  - [iconExtraLarge] = `icon.xl` 48dp -- "Illustrative UI moments"
     *
     * `icon.sm` was MISSING until the Discover card needed it, and its absence is why the feed's
     * like and comment controls were drawn at 16dp: with only 16 and 24 on the shelf, 16 was the
     * one that did not overpower the card, and the primary interaction on the feed ended up
     * wearing the metadata size. A ladder with a rung missing is a ladder that gets misused.
     */
    val iconSmall: Dp = 16.dp

    /**
     * The delivery tick beside a 10sp timestamp: 12dp, BELOW §9.4's ladder and deliberately so.
     *
     * The ladder starts at `icon.xs` 16dp for "metadata, small inline icons", and 16dp was right
     * while the timestamp beside it was `caption` 12sp. The conversation's metadata is
     * [com.swetdot.swetgram.ui.theme.SwetgramCaptionSmall] 10sp now (DECISIONS.md §25), and a 16dp
     * tick against 10sp text is a mark taller than the words it belongs to -- the check would read
     * as the subject of the row rather than as a footnote on it.
     *
     * 12dp RATHER THAN THE 8dp A LITERAL HALVING WOULD GIVE. The tick is the ONLY thing that carries
     * delivery state: `MessageState` has no DELIVERED rung, so one check against two checks is the
     * whole model (SDS §22.2 wants the shape to carry it, which means the shape has to survive).
     * At 12dp the drawn mark is about 7.4dp across and the second check of a read pair is offset by
     * 3.8dp, so the two are plainly two; at 8dp that offset is 2.6dp and the pair smears into one
     * thick mark at the exact moment it is meant to say "read". 12dp also keeps the stroke at
     * 1.08dp, still above the 1dp [borderHairline] the app draws elsewhere, so the mark does not
     * thin below anything else on the screen.
     *
     * A multiple of 4, like every other dimension here, and 0.75x its old size against text that
     * went to 0.83x -- the tick shrinks slightly harder than the words, which is what puts it back
     * in proportion.
     */
    val iconMetaTick: Dp = 12.dp
    val iconSmallMedium: Dp = 20.dp
    val iconMedium: Dp = 24.dp
    val iconLarge: Dp = 32.dp
    val iconExtraLarge: Dp = 48.dp
    val borderHairline: Dp = 1.dp
}

/**
 * The Material3 [Shapes] built from the radius scale.
 *
 * Material maps its own components onto these five slots, so pointing them at the SDS values is
 * what stops a stock `Card` or `TextField` arriving with a radius nobody chose. It does NOT
 * reach `Button`, which sources a pill shape from its own token -- that one has to be passed
 * explicitly wherever a button is built.
 */
val SwetgramShapes = Shapes(
    extraSmall = RoundedCornerShape(Radius.xs),
    small = RoundedCornerShape(Radius.sm),
    medium = RoundedCornerShape(Radius.md),
    large = RoundedCornerShape(Radius.lg),
    extraLarge = RoundedCornerShape(Radius.xl),
)

/**
 * SDS §19.2 durations, in milliseconds.
 *
 * These have to be stated somewhere because the alternative is not "no animation" -- it is the
 * navigation library's own defaults, which differ per platform: 700ms fade on Android and
 * desktop, 200ms slide on iOS. That is three times the specified length on one platform and a
 * different gesture entirely on the other, from the same code, which §23.1 exists to prevent.
 */
object Motion {
    /** Tiny feedback: the pressed state of a button, a row, a tab. */
    const val instant: Int = 80

    /** Buttons and state changes: enable/disable, a badge arriving, a tab tint moving. */
    const val fast: Int = 150

    /** Screen transitions. §2.1: "keep transitions under 250ms in most cases." */
    const val normal: Int = 220

    /** Larger modals, rare emphasis. */
    const val slow: Int = 300

    /**
     * Half the skeleton breathe, derived rather than invented -- see
     * [swetSkeletonPulse][com.swetdot.swetgram.ui.design.swetSkeletonPulse].
     *
     * §19.2's four tokens all describe how long a TRANSITION takes, and none of them describes a
     * resting rhythm; at `slow` a loading placeholder strobes. Expressing it as a multiple of the
     * scale keeps it on the same arithmetic as everything else rather than adding a fifth free
     * number that the next person has to guess the provenance of.
     */
    const val skeletonPulse: Int = slow * 3
}
